import '@playcanvas/web-components';
import { Asset, Color, Entity, EventHandler, MiniStats, Quat, ShaderChunks, Vec3 } from 'playcanvas';
import { XrControllers } from 'playcanvas/scripts/esm/xr-controllers.mjs';
import { XrNavigation } from 'playcanvas/scripts/esm/xr-navigation.mjs';

import { migrateSettings } from './data-migrations.js';
import { observe } from './observe.js';
import { Viewer } from './viewer.js';

// 全局状态
let currentFile = null;
let currentCoordinateSystem = 'lidar';
let app = null;
let viewer = null;

// override global pick to pack depth instead of meshInstance id
const pickDepthGlsl = /* glsl */ `
vec4 packFloat(float depth) {
    uvec4 u = (uvec4(floatBitsToUint(depth)) >> uvec4(0u, 8u, 16u, 24u)) & 0xffu;
    return vec4(u) / 255.0;
}
vec4 getPickOutput() {
    return packFloat(gl_FragCoord.z);
}
`;

const pickDepthWgsl = /* wgsl */ `
    fn packFloat(depth: f32) -> vec4f {
        let u: vec4<u32> = (vec4<u32>(bitcast<u32>(depth)) >> vec4<u32>(0u, 8u, 16u, 24u)) & vec4<u32>(0xffu);
        return vec4f(u) / 255.0;
    }

    fn getPickOutput() -> vec4f {
        return packFloat(pcPosition.z);
    }
`;

// temporary vector
const v = new Vec3();

// get experience parameters
const params = window.sse?.params ?? {};

// displays a blurry poster image which resolves to sharp during loading
const initPoster = (events) => {
    const element = document.getElementById('poster');
    const blur = progress => `blur(${Math.floor((100 - progress) * 0.4)}px)`;

    events.on('progress:changed', (progress) => {
        element.style.filter = blur(progress);
    });

    events.on('firstFrame', () => {
        element.style.display = 'none';
    });
};

// On entering/exiting AR, we need to set the camera clear color to transparent black
const initXr = (app, cameraElement, state, events) => {
    // initialize ar/vr
    app.xr.on('available:immersive-ar', (available) => {
        state.hasAR = available;
    });
    app.xr.on('available:immersive-vr', (available) => {
        state.hasVR = available;
    });

    const parent = cameraElement.parentElement.entity;
    const camera = cameraElement.entity;
    const clearColor = new Color();

    const parentPosition = new Vec3();
    const parentRotation = new Quat();
    const cameraPosition = new Vec3();
    const cameraRotation = new Quat();
    const angles = new Vec3();

    parent.script.create(XrControllers);
    parent.script.create(XrNavigation);

    app.xr.on('start', () => {
        app.autoRender = true;

        // cache original camera rig positions and rotations
        parentPosition.copy(parent.getPosition());
        parentRotation.copy(parent.getRotation());
        cameraPosition.copy(camera.getPosition());
        cameraRotation.copy(camera.getRotation());

        cameraRotation.getEulerAngles(angles);

        // copy transform to parent to XR/VR mode starts in the right place
        parent.setPosition(cameraPosition.x, 0, cameraPosition.z);
        parent.setEulerAngles(0, angles.y, 0);

        if (app.xr.type === 'immersive-ar') {
            clearColor.copy(camera.camera.clearColor);
            camera.camera.clearColor = new Color(0, 0, 0, 0);
        }
    });

    app.xr.on('end', () => {
        app.autoRender = false;

        // restore camera to pre-XR state
        parent.setPosition(parentPosition);
        parent.setRotation(parentRotation);
        camera.setPosition(cameraPosition);
        camera.setRotation(cameraRotation);

        if (app.xr.type === 'immersive-ar') {
            camera.camera.clearColor = clearColor;
        }
    });

    events.on('startAR', () => {
        app.xr.start(app.root.findComponent('camera'), 'immersive-ar', 'local-floor');
    });

    events.on('startVR', () => {
        app.xr.start(app.root.findComponent('camera'), 'immersive-vr', 'local-floor');
    });

    events.on('inputEvent', (event) => {
        if (event === 'cancel' && app.xr.active) {
            app.xr.end();
        }
    });
};

// 加载PLY文件
const loadPlyFile = async (file, coordinateSystem = 'lidar', eventsSystem = null) => {
    return new Promise((resolve, reject) => {
        console.log('loadPlyFile: 开始加载文件', file.name, '坐标系:', coordinateSystem);
        
        // 清除现有的gsplat实体和相关资产
        const existingGsplat = app.root.findByName('gsplat');
        if (existingGsplat) {
            console.log('loadPlyFile: 清除现有的gsplat实体');
            existingGsplat.destroy();
        }
        
        // 清理之前的PLY资产
        const existingAssets = app.assets.filter(asset => asset.type === 'gsplat');
        existingAssets.forEach(asset => {
            console.log('loadPlyFile: 清除现有资产', asset.name);
            app.assets.remove(asset);
        });

        // 创建文件URL
        const fileURL = URL.createObjectURL(file);
        console.log('loadPlyFile: 创建文件URL', fileURL);

        // 创建新的asset
        const asset = new Asset(file.name, 'gsplat', {
            url: fileURL,
            filename: file.name
        });

        let progressInterval;

        asset.on('progress', (received, total) => {
            const progress = Math.floor((received / total) * 100);
            console.log('loadPlyFile: 加载进度', progress + '%');
            
            // 使用events系统更新进度
            if (eventsSystem) {
                eventsSystem.fire('progress:changed', progress);
            } else {
                // 备用：直接更新进度显示
                const loadingText = document.getElementById('loadingText');
                if (loadingText) {
                    loadingText.textContent = `AIGSplat 加载中... ${progress}%`;
                }
                
                const loadingBar = document.getElementById('loadingBar');
                if (loadingBar) {
                    loadingBar.innerHTML = '';
                    const progressElement = document.createElement('div');
                    progressElement.className = 'loading-progress';
                    progressElement.style.width = `${progress}%`;
                    loadingBar.appendChild(progressElement);
                }
            }
        });

        asset.on('load', () => {
            console.log('loadPlyFile: 资产加载完成');
            
            if (progressInterval) {
                clearInterval(progressInterval);
            }

            // 设置进度为100%
            if (eventsSystem) {
                eventsSystem.fire('progress:changed', 100);
            } else {
                const loadingText = document.getElementById('loadingText');
                if (loadingText) {
                    loadingText.textContent = `AIGSplat 加载中... 100%`;
                }
                
                const loadingBar = document.getElementById('loadingBar');
                if (loadingBar) {
                    loadingBar.innerHTML = '';
                    const progressElement = document.createElement('div');
                    progressElement.className = 'loading-progress';
                    progressElement.style.width = '100%';
                    loadingBar.appendChild(progressElement);
                }
            }

            try {
                const entity = new Entity('gsplat');
                
                // 根据坐标系类型设置旋转
                let rotation;
                switch (coordinateSystem) {
                    case 'lidar':
                        rotation = [-90, 0, 0];  // Z轴向上 -> Y轴向上
                        break;
                    case 'colmap':
                        rotation = [0, 0, 180];  // COLMAP坐标系翻转
                        break;
                    default:
                        rotation = [-90, 0, 0];  // 默认使用lidar
                        break;
                }
                
                console.log(`loadPlyFile: 坐标系类型: ${coordinateSystem}, 旋转: [${rotation.join(', ')}]`);
                entity.setLocalEulerAngles(...rotation);
                
                entity.addComponent('gsplat', { asset });
                app.root.addChild(entity);
                
                console.log('loadPlyFile: gsplat实体创建完成');
                
                // 释放临时URL
                URL.revokeObjectURL(fileURL);
                
                resolve(entity);
            } catch (error) {
                console.error('loadPlyFile: 创建实体时出错:', error);
                URL.revokeObjectURL(fileURL);
                reject(error);
            }
        });

        asset.on('error', (err) => {
            console.error('loadPlyFile: 资产加载失败:', err);
            
            if (progressInterval) {
                clearInterval(progressInterval);
            }
            
            // 释放临时URL
            URL.revokeObjectURL(fileURL);
            reject(err);
        });

        console.log('loadPlyFile: 添加资产到管理器');
        app.assets.add(asset);
        
        console.log('loadPlyFile: 开始加载资产');
        
        // 初始化进度为0%
        if (eventsSystem) {
            eventsSystem.fire('progress:changed', 0);
        } else {
            const loadingText = document.getElementById('loadingText');
            if (loadingText) {
                loadingText.textContent = 'AIGSplat 加载中... 0%';
            }
            
            const loadingBar = document.getElementById('loadingBar');
            if (loadingBar) {
                loadingBar.innerHTML = '';
                const progressElement = document.createElement('div');
                progressElement.className = 'loading-progress';
                progressElement.style.width = '0%';
                loadingBar.appendChild(progressElement);
            }
        }
        
        app.assets.load(asset);
        
        // 设置一个备用的进度更新，以防progress事件不触发
        let fakeProgress = 0;
        progressInterval = setInterval(() => {
            if (asset.loading) {
                fakeProgress = Math.min(fakeProgress + 2, 95); // 不超过95%，留给真实进度
                if (fakeProgress < 95) {
                    if (eventsSystem) {
                        eventsSystem.fire('progress:changed', fakeProgress);
                    } else {
                        const loadingText = document.getElementById('loadingText');
                        const loadingBar = document.getElementById('loadingBar');
                        if (loadingText) {
                            loadingText.textContent = `AIGSplat 加载中... ${fakeProgress}%`;
                        }
                        if (loadingBar) {
                            loadingBar.innerHTML = '';
                            const progressElement = document.createElement('div');
                            progressElement.className = 'loading-progress';
                            progressElement.style.width = `${fakeProgress}%`;
                            loadingBar.appendChild(progressElement);
                        }
                    }
                }
            } else {
                clearInterval(progressInterval);
            }
        }, 100);
    });
};

// 显示坐标系切换提示
const showCoordinateChangeNotification = (coordinateSystem) => {
    const notification = document.createElement('div');
    notification.className = 'coordinate-notification';
    notification.innerHTML = `
        <div class="notification-content">
            <div class="notification-icon">🎯</div>
            <div class="notification-text">
                <strong>坐标系已切换</strong>
                <span>${coordinateSystem === 'lidar' ? '🔍 激光雷达扫描' : '📸 COLMAP重建'}</span>
            </div>
        </div>
    `;
    
    document.body.appendChild(notification);
    
    // 显示动画
    setTimeout(() => {
        notification.classList.add('show');
    }, 100);
    
    // 3秒后自动移除
    setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => {
            document.body.removeChild(notification);
        }, 300);
    }, 3000);
};

// 更新坐标系按钮的提示文本
const updateCoordinateButtonTooltip = (coordinateSystem) => {
    const coordinateBtn = document.getElementById('coordinateBtn');
    if (coordinateBtn) {
        const displayText = coordinateSystem === 'lidar' ? '激光雷达扫描' : 'COLMAP重建';
        coordinateBtn.setAttribute('title', `坐标系设置 - 当前：${displayText}`);
    }
};

// 更新当前坐标系显示文本
const updateCurrentCoordinateDisplay = (coordinateSystem) => {
    const currentCoordinateText = document.getElementById('currentCoordinateText');
    if (currentCoordinateText) {
        const displayText = coordinateSystem === 'lidar' ? '激光雷达扫描' : 'COLMAP重建';
        currentCoordinateText.textContent = `当前：${displayText}`;
    }
};

// 同步坐标系选择器状态
const syncCoordinateSelectors = (selectedValue) => {
    // 同步拖拽区域的选择器
    const dropZoneRadios = document.querySelectorAll('input[name="coordinateSystem"]');
    dropZoneRadios.forEach(radio => {
        radio.checked = radio.value === selectedValue;
    });
    
    // 同步面板中的选择器
    const panelRadios = document.querySelectorAll('input[name="coordinateSystemPanel"]');
    panelRadios.forEach(radio => {
        radio.checked = radio.value === selectedValue;
    });
    
    // 更新显示文本
    updateCurrentCoordinateDisplay(selectedValue);
    
    // 更新工具栏按钮提示
    updateCoordinateButtonTooltip(selectedValue);
    
    // 更新状态显示
    window.updateCoordinateStatus();
};

// 初始化文件拖拽功能
const initFileDropZone = (eventsSystem = null) => {
    // 等待一小段时间确保DOM完全加载
    setTimeout(() => {
        const dropZone = document.getElementById('dropZone');
        const fileInput = document.getElementById('fileInput');
        const selectFileBtn = document.getElementById('selectFileBtn');
        const fileName = document.getElementById('fileName');

        console.log('初始化文件拖拽功能...', { dropZone, fileInput, selectFileBtn, fileName });

        if (!dropZone || !fileInput || !selectFileBtn || !fileName) {
            console.error('拖拽区域元素未找到:', {
                dropZone: !!dropZone,
                fileInput: !!fileInput,
                selectFileBtn: !!selectFileBtn,
                fileName: !!fileName
            });
            return;
        }

        // 防止默认的拖拽行为
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, preventDefaults, false);
            document.body.addEventListener(eventName, preventDefaults, false);
        });

        function preventDefaults(e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // 拖拽视觉反馈
        ['dragenter', 'dragover'].forEach(eventName => {
            dropZone.addEventListener(eventName, highlight, false);
        });

        ['dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, unhighlight, false);
        });

        function highlight(e) {
            console.log('拖拽高亮');
            dropZone.classList.add('drag-over');
        }

        function unhighlight(e) {
            console.log('取消拖拽高亮');
            dropZone.classList.remove('drag-over');
        }

        // 处理文件拖拽
        dropZone.addEventListener('drop', handleDrop, false);

        function handleDrop(e) {
            console.log('文件被拖拽放下');
            const dt = e.dataTransfer;
            const files = dt.files;

            if (files.length > 0) {
                const file = files[0];
                console.log('拖拽的文件:', file.name, file.type);
                if (file.name.toLowerCase().endsWith('.ply')) {
                    handleFileSelect(file);
                } else {
                    alert('请选择PLY文件');
                }
            }
        }

        // 处理文件选择按钮
        selectFileBtn.addEventListener('click', () => {
            console.log('点击选择文件按钮');
            fileInput.click();
        });

        fileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            console.log('通过文件选择器选择的文件:', file?.name);
            if (file) {
                // 验证文件格式
                if (file.name.toLowerCase().endsWith('.ply')) {
                    handleFileSelect(file);
                } else {
                    alert('请选择PLY格式的点云文件');
                    // 清空文件选择器
                    fileInput.value = '';
                }
            }
        });

        // 处理文件选择
        async function handleFileSelect(file) {
            console.log('开始处理文件:', file.name);
            currentFile = file;
                            fileName.textContent = `AIGSplat: ${file.name}`;
            
            // 获取坐标系设置
            const coordinateRadios = document.querySelectorAll('input[name="coordinateSystem"]');
            for (const radio of coordinateRadios) {
                if (radio.checked) {
                    currentCoordinateSystem = radio.value;
                    break;
                }
            }

            console.log('使用坐标系:', currentCoordinateSystem);

            // 隐藏拖拽区域
            dropZone.classList.add('hidden');
            dropZone.style.display = 'none';
            
            // 显示加载指示器
            const loadingWrap = document.getElementById('loadingWrap');
            if (loadingWrap) {
                loadingWrap.classList.remove('hidden');
            }
            
            try {
                // 加载PLY文件
                console.log('开始加载PLY文件...');
                await loadPlyFile(file, currentCoordinateSystem, eventsSystem);
                
                // 等待加载完成后初始化viewer
                if (viewer) {
                    console.log('重新初始化viewer...');
                    viewer.initialize();
                }
                
                console.log('PLY文件加载成功');
                
                // 隐藏加载指示器
                if (loadingWrap) {
                    loadingWrap.classList.add('hidden');
                }
                
                // 启用控制面板切换按钮
                const controlsToggleBtn = document.getElementById('controlsToggleBtn');
                if (controlsToggleBtn) {
                    controlsToggleBtn.disabled = false;
                    controlsToggleBtn.style.opacity = '1';
                }
                
                // 启用动画按钮
                const startAnimationBtn = document.getElementById('startAnimationBtn');
                const stopAnimationBtn = document.getElementById('stopAnimationBtn');
                if (startAnimationBtn) {
                    startAnimationBtn.disabled = false;
                }
                if (stopAnimationBtn) {
                    stopAnimationBtn.disabled = true;
                }
                
                // 启用工具栏自动旋转按钮
                const autoRotateBtn = document.getElementById('autoRotateBtn');
                if (autoRotateBtn) {
                    autoRotateBtn.disabled = false;
                }
                
                // 显示返回首页按钮
                const homeBtn = document.getElementById('homeBtn');
                if (homeBtn) {
                    homeBtn.style.display = 'block';
                }
                
                // 重置加载文本
                const loadingText = document.getElementById('loadingText');
                if (loadingText) {
                    loadingText.textContent = 'AIGSplat 准备中...';
                }
                
            } catch (error) {
                console.error('加载文件失败:', error);
                alert('加载文件失败，请检查文件格式');
                
                // 重新显示拖拽区域
                dropZone.classList.remove('hidden');
                dropZone.style.display = 'flex';
                if (loadingWrap) {
                    loadingWrap.classList.add('hidden');
                }
                
                // 重置加载文本
                const loadingText = document.getElementById('loadingText');
                if (loadingText) {
                    loadingText.textContent = 'AIGSplat 准备中...';
                }
            }
        }

        // 监听坐标系设置变化
        const coordinateRadios = document.querySelectorAll('input[name="coordinateSystem"]');
        console.log('找到坐标系单选按钮数量:', coordinateRadios.length);
        coordinateRadios.forEach((radio, index) => {
            console.log(`绑定坐标系按钮 ${index + 1}:`, radio.value);
            radio.addEventListener('change', (e) => {
                console.log('坐标系设置变化:', e.target.value);
                currentCoordinateSystem = e.target.value;
                
                // 同步面板中的选择器状态
                syncCoordinateSelectors(currentCoordinateSystem);
            });
            
            // 添加点击事件作为备用
            radio.addEventListener('click', (e) => {
                console.log('坐标系按钮被点击:', e.target.value);
                currentCoordinateSystem = e.target.value;
                
                // 同步面板中的选择器状态
                syncCoordinateSelectors(currentCoordinateSystem);
            });
        });

        // 同时监听label点击事件
        const coordinateLabels = document.querySelectorAll('.coordinate-option');
        console.log('找到坐标系标签数量:', coordinateLabels.length);
        coordinateLabels.forEach((label, index) => {
            // 添加鼠标进入和离开的调试信息
            label.addEventListener('mouseenter', () => {
                console.log('鼠标进入坐标系选项:', index);
            });
            
            label.addEventListener('mouseleave', () => {
                console.log('鼠标离开坐标系选项:', index);
            });
            
            label.addEventListener('click', (e) => {
                console.log('坐标系标签被点击:', index, e);
                e.preventDefault();
                e.stopPropagation();
                
                const radio = label.querySelector('input[type="radio"]');
                if (radio) {
                    console.log('手动触发radio选择:', radio.value);
                    radio.checked = true;
                    // 判断是面板还是拖拽区域
                    const inPanel = !!label.closest('#coordinatePanel');
                    if (inPanel) {
                        window.pendingCoordinateSystem = radio.value;
                        window.updateConfirmButton();
                    } else {
                        currentCoordinateSystem = radio.value;
                        // 同步面板中的选择器状态
                        syncCoordinateSelectors(currentCoordinateSystem);
                    }
                    // 触发change事件
                    radio.dispatchEvent(new Event('change', { bubbles: true }));
                }
            });
            
            // 直接监听整个label的所有事件作为调试
            ['mousedown', 'mouseup', 'pointerdown', 'pointerup'].forEach(eventType => {
                label.addEventListener(eventType, (e) => {
                    console.log(`坐标系选项${index}事件:`, eventType, e);
                });
            });
        });

        console.log('文件拖拽功能初始化完成');
        
        // 测试坐标系选择器是否正确绑定
        setTimeout(() => {
            const testLabels = document.querySelectorAll('.coordinate-option');
            console.log('延迟检查：找到坐标系选项数量:', testLabels.length);
            testLabels.forEach((label, index) => {
                const computedStyle = window.getComputedStyle(label);
                console.log(`选项${index}样式:`, {
                    pointerEvents: computedStyle.pointerEvents,
                    zIndex: computedStyle.zIndex,
                    position: computedStyle.position
                });
            });
        }, 500);
    }, 100);
};

// 初始化新的UI交互
const initNewUI = (events, state) => {
    // 工具栏按钮
    const homeBtn = document.getElementById('homeBtn');
    const newFileBtn = document.getElementById('newFile');
    const coordinateBtn = document.getElementById('coordinateBtn');
    const autoRotateBtn = document.getElementById('autoRotateBtn');
    const fullscreenBtn = document.getElementById('fullscreenBtn');
    const infoBtn = document.getElementById('infoBtn');

    // 控制面板
    const controlsToggleBtn = document.getElementById('controlsToggleBtn');
    const orbitModeBtn = document.getElementById('orbitMode');
    const flyModeBtn = document.getElementById('flyMode');
    const highQualityBtn = document.getElementById('highQuality');
    const lowQualityBtn = document.getElementById('lowQuality');
    const frameBtnNew = document.getElementById('frameBtn');
    const resetBtnNew = document.getElementById('resetBtn');
    const startAnimationBtn = document.getElementById('startAnimationBtn');
    const stopAnimationBtn = document.getElementById('stopAnimationBtn');

    // 面板
    const coordinatePanel = document.getElementById('coordinatePanel');
    const helpPanel = document.getElementById('helpPanel');
    const closeCoordinatePanel = document.getElementById('closeCoordinatePanel');
    const closeHelpPanel = document.getElementById('closeHelpPanel');

    // 返回首页按钮
    if (homeBtn) {
        homeBtn.addEventListener('click', () => {
            console.log('点击返回首页按钮');
            
            // 清除现有的点云
            const existingGsplat = app.root.findByName('gsplat');
            if (existingGsplat) {
                existingGsplat.destroy();
            }
            
            // 显示拖拽区域
            const dropZone = document.getElementById('dropZone');
            if (dropZone) {
                dropZone.classList.remove('hidden');
                dropZone.style.display = 'flex';
            }
            
            // 隐藏加载指示器
            const loadingWrap = document.getElementById('loadingWrap');
            if (loadingWrap) {
                loadingWrap.classList.add('hidden');
            }
            
            // 隐藏返回首页按钮
            homeBtn.style.display = 'none';
            
            // 重置文件名显示
            const fileName = document.getElementById('fileName');
            if (fileName) {
                fileName.textContent = 'AIGSplat - 准备加载文件';
            }
            
            // 重置全局状态
            currentFile = null;
            currentCoordinateSystem = 'lidar';
            
            // 重置并同步坐标系选择器
            syncCoordinateSelectors('lidar');
            
            // 禁用控制面板切换按钮
            const controlsToggleBtn = document.getElementById('controlsToggleBtn');
            if (controlsToggleBtn) {
                controlsToggleBtn.disabled = true;
                controlsToggleBtn.style.opacity = '0.5';
            }
            // 关闭控制面板
            const controlPanel = document.getElementById('controlPanel');
            if (controlPanel) {
                controlPanel.classList.remove('open');
            }
            const toolbar = document.getElementById('topToolbar');
            if (toolbar) {
                toolbar.classList.remove('controls-open');
            }
        });
    }

    // 新文件按钮
    if (newFileBtn) {
        newFileBtn.addEventListener('click', () => {
            console.log('点击新文件按钮');
            const dropZone = document.getElementById('dropZone');
            const loadingWrap = document.getElementById('loadingWrap');
            
            // 显示拖拽区域
            if (dropZone) {
                dropZone.classList.remove('hidden');
                dropZone.style.display = 'flex';
            }
            
            // 隐藏加载指示器
            if (loadingWrap) {
                loadingWrap.classList.add('hidden');
            }
            
            // 重置文件名显示
            const fileName = document.getElementById('fileName');
            if (fileName) {
                fileName.textContent = 'AIGSplat - 准备加载文件';
            }
            
            // 重置全局状态
            currentFile = null;
            currentCoordinateSystem = 'lidar';
            
            // 重置并同步坐标系选择器
            syncCoordinateSelectors('lidar');
            
            // 重置待处理的坐标系选择
            if (window.pendingCoordinateSystem !== undefined) {
                window.pendingCoordinateSystem = 'lidar';
            }
            
            // 禁用控制面板切换按钮
            const controlsToggleBtn = document.getElementById('controlsToggleBtn');
            if (controlsToggleBtn) {
                controlsToggleBtn.disabled = true;
                controlsToggleBtn.style.opacity = '0.5';
            }
            
            // 禁用动画按钮
            const startAnimationBtn = document.getElementById('startAnimationBtn');
            const stopAnimationBtn = document.getElementById('stopAnimationBtn');
            if (startAnimationBtn) {
                startAnimationBtn.disabled = true;
                startAnimationBtn.classList.remove('active');
            }
            if (stopAnimationBtn) {
                stopAnimationBtn.disabled = true;
                stopAnimationBtn.classList.remove('active');
            }
            
            // 禁用工具栏自动旋转按钮
            const autoRotateBtn = document.getElementById('autoRotateBtn');
            if (autoRotateBtn) {
                autoRotateBtn.disabled = true;
                autoRotateBtn.classList.remove('auto-rotating');
                autoRotateBtn.title = '自动旋转';
            }
            
            // 关闭控制面板
            const controlPanel = document.getElementById('controlPanel');
            if (controlPanel) {
                controlPanel.classList.remove('open');
            }
            const toolbar = document.getElementById('topToolbar');
            if (toolbar) {
                toolbar.classList.remove('controls-open');
            }
            
            // 隐藏返回首页按钮
            const homeBtn = document.getElementById('homeBtn');
            if (homeBtn) {
                homeBtn.style.display = 'none';
            }
        });
    }

    // 坐标系设置按钮
    if (coordinateBtn) {
        coordinateBtn.addEventListener('click', () => {
            console.log('=== 坐标系设置按钮被点击 ===');
            console.log('当前文件:', currentFile ? currentFile.name : '无文件');
            console.log('当前坐标系:', currentCoordinateSystem);
            
            // 确保面板中的选择器状态正确
            const panelRadios = document.querySelectorAll('input[name="coordinateSystemPanel"]');
            console.log('面板中的radio按钮数量:', panelRadios.length);
            panelRadios.forEach((radio, index) => {
                console.log(`面板radio ${index}:`, radio.value, '选中:', radio.checked);
                // 强制同步状态
                radio.checked = radio.value === currentCoordinateSystem;
            });
            
            coordinatePanel.classList.remove('hidden');
            console.log('坐标系面板已显示');
            
            // 触发面板打开事件
            events.fire('coordinatePanel:opened');
        });
    }

    // 工具栏自动旋转按钮
    if (autoRotateBtn) {
        autoRotateBtn.addEventListener('click', () => {
            if (autoRotateBtn.classList.contains('auto-rotating')) {
                // 停止自动旋转
                console.log('工具栏：停止自动旋转');
                events.fire('stopAutoRotate');
                autoRotateBtn.classList.remove('auto-rotating');
                autoRotateBtn.title = '自动旋转';
                
                // 同步控制面板按钮状态
                const startAnimationBtn = document.getElementById('startAnimationBtn');
                const stopAnimationBtn = document.getElementById('stopAnimationBtn');
                if (startAnimationBtn) {
                    startAnimationBtn.disabled = false;
                    startAnimationBtn.classList.remove('active');
                }
                if (stopAnimationBtn) {
                    stopAnimationBtn.disabled = true;
                    stopAnimationBtn.classList.remove('active');
                }
            } else {
                // 开始自动旋转
                console.log('工具栏：开始自动旋转');
                events.fire('startAutoRotate');
                autoRotateBtn.classList.add('auto-rotating');
                autoRotateBtn.title = '停止旋转';
                
                // 同步控制面板按钮状态
                const startAnimationBtn = document.getElementById('startAnimationBtn');
                const stopAnimationBtn = document.getElementById('stopAnimationBtn');
                if (startAnimationBtn) {
                    startAnimationBtn.disabled = true;
                    startAnimationBtn.classList.add('active');
                }
                if (stopAnimationBtn) {
                    stopAnimationBtn.disabled = false;
                    stopAnimationBtn.classList.remove('active');
                }
            }
        });
    }

    // 全屏按钮
    if (fullscreenBtn) {
        fullscreenBtn.addEventListener('click', () => {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else {
                document.documentElement.requestFullscreen();
            }
        });
    }

    // 监听全屏状态变化
    document.addEventListener('fullscreenchange', () => {
        const toolbar = document.getElementById('topToolbar');
        if (toolbar) {
            if (document.fullscreenElement) {
                toolbar.classList.add('fullscreen-hidden');
            } else {
                toolbar.classList.remove('fullscreen-hidden');
            }
        }
    });

    // 控制面板切换按钮
    if (controlsToggleBtn) {
        controlsToggleBtn.addEventListener('click', () => {
            const controlPanel = document.getElementById('controlPanel');
            const toolbar = document.getElementById('topToolbar');
            
            if (controlPanel.classList.contains('open')) {
                controlPanel.classList.remove('open');
                toolbar.classList.remove('controls-open');
            } else {
                controlPanel.classList.add('open');
                toolbar.classList.add('controls-open');
            }
        });
    }

    // 帮助按钮
    if (infoBtn) {
        infoBtn.addEventListener('click', () => {
            helpPanel.classList.remove('hidden');
        });
    }

    // 相机模式切换
    if (orbitModeBtn) {
        orbitModeBtn.addEventListener('click', () => {
            state.cameraMode = 'orbit';
            orbitModeBtn.classList.add('active');
            flyModeBtn.classList.remove('active');
            // 触发原有的轨道模式按钮点击
            const orbitBtn = document.getElementById('orbit');
            if (orbitBtn) orbitBtn.click();
        });
    }

    if (flyModeBtn) {
        flyModeBtn.addEventListener('click', () => {
            state.cameraMode = 'fly';
            flyModeBtn.classList.add('active');
            orbitModeBtn.classList.remove('active');
            // 触发原有的飞行模式按钮点击
            const flyBtn = document.getElementById('fly');
            if (flyBtn) flyBtn.click();
        });
    }

    // 渲染质量切换
    if (highQualityBtn) {
        highQualityBtn.addEventListener('click', () => {
            state.hqMode = true;
            highQualityBtn.classList.add('active');
            lowQualityBtn.classList.remove('active');
            // 触发原有的高质量按钮点击
            const highBtn = document.getElementById('high');
            if (highBtn) highBtn.click();
        });
    }

    if (lowQualityBtn) {
        lowQualityBtn.addEventListener('click', () => {
            state.hqMode = false;
            lowQualityBtn.classList.add('active');
            highQualityBtn.classList.remove('active');
            // 触发原有的低质量按钮点击
            const lowBtn = document.getElementById('low');
            if (lowBtn) lowBtn.click();
        });
    }

    // 适应窗口和重置按钮
    if (frameBtnNew) {
        frameBtnNew.addEventListener('click', () => {
            events.fire('inputEvent', 'frame');
        });
    }

    if (resetBtnNew) {
        resetBtnNew.addEventListener('click', () => {
            events.fire('inputEvent', 'reset');
        });
    }

    // 自动动画按钮
    if (startAnimationBtn) {
        startAnimationBtn.addEventListener('click', () => {
            console.log('控制面板：开始自动旋转动画');
            events.fire('startAutoRotate');
            
            // 更新按钮状态
            startAnimationBtn.disabled = true;
            startAnimationBtn.classList.add('active');
            stopAnimationBtn.disabled = false;
            stopAnimationBtn.classList.remove('active');
            
            // 同步工具栏按钮状态
            const autoRotateBtn = document.getElementById('autoRotateBtn');
            if (autoRotateBtn) {
                autoRotateBtn.classList.add('auto-rotating');
                autoRotateBtn.title = '停止旋转';
            }
        });
    }

    if (stopAnimationBtn) {
        stopAnimationBtn.addEventListener('click', () => {
            console.log('控制面板：停止自动旋转动画');
            events.fire('stopAutoRotate');
            
            // 更新按钮状态
            startAnimationBtn.disabled = false;
            startAnimationBtn.classList.remove('active');
            stopAnimationBtn.disabled = true;
            stopAnimationBtn.classList.remove('active');
            
            // 同步工具栏按钮状态
            const autoRotateBtn = document.getElementById('autoRotateBtn');
            if (autoRotateBtn) {
                autoRotateBtn.classList.remove('auto-rotating');
                autoRotateBtn.title = '自动旋转';
            }
        });
    }

    // 关闭面板
    if (closeCoordinatePanel) {
        closeCoordinatePanel.addEventListener('click', () => {
            coordinatePanel.classList.add('hidden');
        });
    }

    if (closeHelpPanel) {
        closeHelpPanel.addEventListener('click', () => {
            helpPanel.classList.add('hidden');
        });
    }

    // 点击面板外部关闭
    [coordinatePanel, helpPanel].forEach(panel => {
        if (panel) {
            panel.addEventListener('click', (e) => {
                if (e.target === panel) {
                    panel.classList.add('hidden');
                }
            });
        }
    });

    // 用于存储面板中选择的坐标系（未确定前的临时选择）
    window.pendingCoordinateSystem = currentCoordinateSystem;
    
    // 坐标系面板设置
    const coordinatePanelRadios = document.querySelectorAll('input[name="coordinateSystemPanel"]');
    console.log('找到坐标系面板单选按钮数量:', coordinatePanelRadios.length);
    
    coordinatePanelRadios.forEach((radio, index) => {
        console.log(`绑定坐标系面板按钮 ${index + 1}:`, radio.value, radio.checked);
        
        radio.addEventListener('change', (e) => {
            const selectedSystem = e.target.value;
            console.log('坐标系面板选择变更:', selectedSystem);
            window.pendingCoordinateSystem = selectedSystem;
            
            // 更新确定按钮状态
            window.updateConfirmButton();
        });
        
        // 添加click事件作为备用
        radio.addEventListener('click', (e) => {
            console.log('坐标系面板按钮被点击:', e.target.value);
        });
    });
    
    // 确定按钮功能
    const confirmCoordinateSwitch = document.getElementById('confirmCoordinateSwitch');
    if (confirmCoordinateSwitch) {
        confirmCoordinateSwitch.addEventListener('click', async () => {
            console.log('=== 确定切换坐标系 ===');
            console.log('当前文件:', currentFile ? currentFile.name : '无');
            console.log('当前坐标系:', currentCoordinateSystem);
            console.log('目标坐标系:', window.pendingCoordinateSystem);
            
            if (!currentFile) {
                alert('请先加载PLY文件再切换坐标系');
                return;
            }
            
            if (window.pendingCoordinateSystem === currentCoordinateSystem) {
                console.log('坐标系未变化，无需切换');
                coordinatePanel.classList.add('hidden');
                return;
            }
            
            // 禁用确定按钮，显示加载状态
            confirmCoordinateSwitch.disabled = true;
            confirmCoordinateSwitch.textContent = '正在切换...';
            
            // 显示加载指示器
            const loadingWrap = document.getElementById('loadingWrap');
            const loadingText = document.getElementById('loadingText');
            if (loadingWrap && loadingText) {
                loadingText.textContent = '正在切换坐标系...';
                loadingWrap.classList.remove('hidden');
            }
            
            try {
                // 执行坐标系切换
                const oldCoordinateSystem = currentCoordinateSystem;
                currentCoordinateSystem = window.pendingCoordinateSystem;
                
                // 同步所有选择器
                syncCoordinateSelectors(currentCoordinateSystem);
                
                // 重新加载文件
                await loadPlyFile(currentFile, currentCoordinateSystem, events);
                
                // 重新初始化viewer
                if (viewer) {
                    viewer.initialize();
                }
                
                // 显示成功通知
                showCoordinateChangeNotification(currentCoordinateSystem);
                
                // 关闭面板
                coordinatePanel.classList.add('hidden');
                
                console.log(`坐标系切换成功: ${oldCoordinateSystem} -> ${currentCoordinateSystem}`);
                
            } catch (error) {
                console.error('坐标系切换失败:', error);
                alert('坐标系切换失败，请重试');
                
                // 恢复之前的选择
                window.pendingCoordinateSystem = currentCoordinateSystem;
                syncCoordinateSelectors(currentCoordinateSystem);
                
            } finally {
                // 恢复按钮状态
                confirmCoordinateSwitch.disabled = false;
                confirmCoordinateSwitch.textContent = '确定切换';
                
                // 隐藏加载指示器
                if (loadingWrap) {
                    loadingWrap.classList.add('hidden');
                }
                
                // 更新状态显示
                window.updateCoordinateStatus();
            }
        });
    }
    
    // 更新确定按钮状态
    window.updateConfirmButton = () => {
        const confirmCoordinateSwitch = document.getElementById('confirmCoordinateSwitch');
        if (confirmCoordinateSwitch) {
            const hasChange = window.pendingCoordinateSystem !== currentCoordinateSystem;
            const hasFile = !!currentFile;
            
            confirmCoordinateSwitch.disabled = !hasChange || !hasFile;
            
            if (!hasFile) {
                confirmCoordinateSwitch.textContent = '请先加载文件';
            } else if (!hasChange) {
                confirmCoordinateSwitch.textContent = '无变化';
            } else {
                confirmCoordinateSwitch.textContent = '确定切换';
            }
        }
    };
    
    // 更新坐标系状态显示
    window.updateCoordinateStatus = () => {
        const currentCoordinateStatus = document.getElementById('currentCoordinateStatus');
        if (currentCoordinateStatus) {
            const displayText = currentCoordinateSystem === 'lidar' ? '激光雷达扫描' : 'COLMAP重建';
            currentCoordinateStatus.textContent = displayText;
        }
    };
    
    // 添加面板级别的点击测试
    if (coordinatePanel) {
        coordinatePanel.addEventListener('click', (e) => {
            console.log('坐标系面板被点击:', e.target.tagName, e.target.className);
            if (e.target.type === 'radio') {
                console.log('直接点击了radio:', e.target.value, e.target.checked);
                // 手动触发change事件
                setTimeout(() => {
                    e.target.dispatchEvent(new Event('change', { bubbles: true }));
                }, 10);
            }
        });
    }
    
    // 初始化状态显示
    window.updateCoordinateStatus();
    window.updateConfirmButton();

    // 监听相机模式变化
    events.on('cameraMode:changed', (mode) => {
        if (mode === 'orbit') {
            orbitModeBtn?.classList.add('active');
            flyModeBtn?.classList.remove('active');
        } else if (mode === 'fly') {
            flyModeBtn?.classList.add('active');
            orbitModeBtn?.classList.remove('active');
        } else if (mode === 'auto-orbit') {
            orbitModeBtn?.classList.add('active');
            flyModeBtn?.classList.remove('active');
        }
    });

    // 监听自动旋转状态变化
    events.on('startAutoRotate', () => {
        const autoRotateBtn = document.getElementById('autoRotateBtn');
        if (autoRotateBtn) {
            autoRotateBtn.classList.add('auto-rotating');
            autoRotateBtn.title = '停止旋转';
        }
    });

    events.on('stopAutoRotate', () => {
        const autoRotateBtn = document.getElementById('autoRotateBtn');
        if (autoRotateBtn) {
            autoRotateBtn.classList.remove('auto-rotating');
            autoRotateBtn.title = '自动旋转';
        }
    });

    // 监听质量模式变化
    events.on('hqMode:changed', (value) => {
        if (value) {
            highQualityBtn?.classList.add('active');
            lowQualityBtn?.classList.remove('active');
        } else {
            lowQualityBtn?.classList.add('active');
            highQualityBtn?.classList.remove('active');
        }
    });

    // 面板打开时同步状态
    events.on('coordinatePanel:opened', () => {
        window.pendingCoordinateSystem = currentCoordinateSystem;
        window.updateCoordinateStatus();
        window.updateConfirmButton();
    });

    // 统一监听面板内的 change 事件，保证状态更新
    if (coordinatePanel) {
        coordinatePanel.addEventListener('change', (e) => {
            if (e.target && e.target.name === 'coordinateSystemPanel') {
                console.log('面板 change 捕获:', e.target.value);
                window.pendingCoordinateSystem = e.target.value;
                window.updateConfirmButton();
            }
        });
    }
};

document.addEventListener('DOMContentLoaded', async () => {
    const appElement = document.querySelector('pc-app');
    app = (await appElement.ready()).app;
    const { graphicsDevice } = app;

    // render skybox as plain equirect
    const glsl = ShaderChunks.get(graphicsDevice, 'glsl');
    glsl.set('skyboxPS', glsl.get('skyboxPS').replace('mapRoughnessUv(uv, mipLevel)', 'uv'));
    glsl.set('pickPS', pickDepthGlsl);

    const wgsl = ShaderChunks.get(graphicsDevice, 'wgsl');
    wgsl.set('skyboxPS', wgsl.get('skyboxPS').replace('mapRoughnessUv(uv, uniform.mipLevel)', 'uv'));
    wgsl.set('pickPS', pickDepthWgsl);

    // 移除自动加载默认内容的逻辑
    // 始终让用户手动选择文件

    const cameraElement = await document.querySelector('pc-entity[name="camera"]').ready();
    const camera = cameraElement.entity;
    const settings = migrateSettings(await window.sse?.settings);
    const events = new EventHandler();
    const state = observe(events, {
        readyToRender: false,       // don't render till this is set
        hqMode: true,
        progress: 0,
        inputMode: 'desktop',       // desktop, touch
        cameraMode: 'orbit',        // orbit, anim, fly
        hasAnimation: false,
        animationDuration: 0,
        animationTime: 0,
        animationPaused: params.noanim,
        hasAR: app.xr.isAvailable('immersive-ar'),
        hasVR: app.xr.isAvailable('immersive-vr'),
        isFullscreen: false,
        uiVisible: true
    });

    // Initialize the load-time poster
    if (window.sse?.poster) {
        initPoster(events);
    }

    // Initialize skybox
    if (params.skyboxUrl) {
        const skyAsset = new Asset('skybox', 'texture', {
            url: params.skyboxUrl
        }, {
            type: 'rgbp',
            mipmaps: false,
            addressu: 'repeat',
            addressv: 'clamp'
        });

        skyAsset.on('load', () => {
            app.scene.envAtlas = skyAsset.resource;
        });

        app.assets.add(skyAsset);
        app.assets.load(skyAsset);
    }

    // Construct ministats
    if (params.ministats) {
        const miniStats = new MiniStats(app);
        miniStats.position = 'topright';
    }

    // Initialize XR support
    initXr(app, cameraElement, state, events);

    // Initialize viewer
    viewer = new Viewer(app, camera, events, state, settings, params);

    // 初始化文件拖拽功能
    initFileDropZone(events);

    // 初始化新的UI交互
    initNewUI(events, state);

    // 初始化时同步坐标系选择器状态
    setTimeout(() => {
        syncCoordinateSelectors(currentCoordinateSystem);
    }, 100);

    // 移除自动加载和初始化的逻辑
    // 始终显示文件拖拽区域，让用户手动选择文件
    console.log('开始初始化界面元素...');
    
    const dropZone = document.getElementById('dropZone');
    if (dropZone) {
        dropZone.classList.remove('hidden');
        dropZone.style.display = 'flex';
        console.log('显示文件拖拽区域 - 成功');
    } else {
        console.error('未找到dropZone元素');
    }
    
    // 确保加载指示器在初始状态下是隐藏的
    const loadingWrap = document.getElementById('loadingWrap');
    if (loadingWrap) {
        loadingWrap.classList.add('hidden');
        console.log('隐藏加载指示器 - 成功');
    } else {
        console.error('未找到loadingWrap元素');
    }
    
    // 确保工具栏和控制面板可见
    const toolbar = document.getElementById('topToolbar');
    if (toolbar) {
        toolbar.style.display = 'flex';
        console.log('显示工具栏 - 成功');
    } else {
        console.error('未找到topToolbar元素');
    }
    
    const controlPanel = document.getElementById('controlPanel');
    if (controlPanel) {
        controlPanel.style.display = 'block';
        console.log('显示控制面板 - 成功');
    } else {
        console.error('未找到controlPanel元素');
    }
    
    console.log('界面初始化完成');
    
    // 只有用户选择文件后才初始化viewer
    // viewer.initialize() 将在文件加载完成后调用

    // Acquire Elements
    const docRoot = document.documentElement;
    const dom = [
        'ui',
        'controlsWrap',
        'arMode', 'vrMode',
        'enterFullscreen', 'exitFullscreen',
        'info', 'infoPanel', 'desktopTab', 'touchTab', 'desktopInfoPanel', 'touchInfoPanel',
        'timelineContainer', 'handle', 'time',
        'buttonContainer',
        'play', 'pause',
        'settings', 'settingsPanel',
        'fly', 'orbit', 'cameraToggleHighlight',
        'high', 'low', 'qualityToggleHighlight',
        'reset', 'frame',
        'loadingText', 'loadingBar',
        'joystickBase', 'joystick'
    ].reduce((acc, id) => {
        acc[id] = document.getElementById(id);
        return acc;
    }, {});

    // Handle loading progress updates
    events.on('progress:changed', (progress) => {
        if (dom.loadingText) {
            dom.loadingText.textContent = `AIGSplat 加载中... ${progress}%`;
        }
        if (dom.loadingBar) {
            // 清除之前的内容
            dom.loadingBar.innerHTML = '';
            
            // 创建进度条元素
            const progressElement = document.createElement('div');
            progressElement.className = 'loading-progress';
            progressElement.style.width = `${progress}%`;
            
            dom.loadingBar.appendChild(progressElement);
        }
    });

    // Hide loading bar once first frame is rendered
    events.on('firstFrame', () => {
        const loadingWrap = document.getElementById('loadingWrap');
        if (loadingWrap) {
            loadingWrap.classList.add('hidden');
        }
    });

    // 简化的事件处理，保持与原有系统的兼容性
    const setupCompatibilityEvents = () => {
        // Fullscreen support
        const hasFullscreenAPI = docRoot.requestFullscreen && document.exitFullscreen;

        const requestFullscreen = () => {
            if (hasFullscreenAPI) {
                docRoot.requestFullscreen();
            } else {
                window.parent.postMessage('requestFullscreen', '*');
                state.isFullscreen = true;
            }
        };

        const exitFullscreen = () => {
            if (hasFullscreenAPI) {
                document.exitFullscreen();
            } else {
                window.parent.postMessage('exitFullscreen', '*');
                state.isFullscreen = false;
            }
        };

        if (hasFullscreenAPI) {
            document.addEventListener('fullscreenchange', () => {
                state.isFullscreen = !!document.fullscreenElement;
            });
        }

        if (dom.enterFullscreen) {
            dom.enterFullscreen.addEventListener('click', requestFullscreen);
        }
        if (dom.exitFullscreen) {
            dom.exitFullscreen.addEventListener('click', exitFullscreen);
        }

        // update UI when fullscreen state changes
        events.on('isFullscreen:changed', (value) => {
            if (dom.enterFullscreen) {
                dom.enterFullscreen.classList[value ? 'add' : 'remove']('hidden');
            }
            if (dom.exitFullscreen) {
                dom.exitFullscreen.classList[value ? 'remove' : 'add']('hidden');
            }
        });

        // HQ mode
        if (dom.high) {
            dom.high.addEventListener('click', () => {
                state.hqMode = true;
            });
        }
        if (dom.low) {
            dom.low.addEventListener('click', () => {
                state.hqMode = false;
            });
        }

        // Camera mode
        if (dom.orbit) {
            dom.orbit.addEventListener('click', () => {
                state.cameraMode = 'orbit';
            });
        }
        if (dom.fly) {
            dom.fly.addEventListener('click', () => {
                state.cameraMode = 'fly';
            });
        }

        // Frame and reset
        if (dom.frame) {
            dom.frame.addEventListener('click', () => {
                events.fire('inputEvent', 'frame');
            });
        }
        if (dom.reset) {
            dom.reset.addEventListener('click', () => {
                events.fire('inputEvent', 'reset');
            });
        }
    };

    setupCompatibilityEvents();

    // 键盘事件处理
    window.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            events.fire('inputEvent', 'cancel', event);
        } else if (!event.ctrlKey && !event.altKey && !event.metaKey) {
            switch (event.key) {
                case 'f':
                    events.fire('inputEvent', 'frame', event);
                    break;
                case 'r':
                    events.fire('inputEvent', 'reset', event);
                    break;
                case ' ':
                    events.fire('inputEvent', 'playPause', event);
                    break;
            }
        }
    });

    // 触摸和鼠标事件处理
    const lastTap = { time: 0, x: 0, y: 0 };
    
    graphicsDevice.canvas.addEventListener('pointerdown', (event) => {
        const now = Date.now();
        const delay = Math.max(0, now - lastTap.time);
        if (delay < 300 &&
            Math.abs(event.clientX - lastTap.x) < 8 &&
            Math.abs(event.clientY - lastTap.y) < 8) {
            events.fire('inputEvent', 'dblclick', event);
            lastTap.time = 0;
        } else {
            lastTap.time = now;
            lastTap.x = event.clientX;
            lastTap.y = event.clientY;
        }
    });

    // update input mode based on pointer event
    ['pointerdown', 'pointermove'].forEach((eventName) => {
        window.addEventListener(eventName, (event) => {
            state.inputMode = event.pointerType === 'touch' ? 'touch' : 'desktop';
        });
    });
});
